// Reporting Phase 1 (CPRN00117-867) — data layer.
// GraphQL queries + pure computation helpers that turn raw Timestream
// datapoints into the shapes each report template renders.

import { gql } from "@apollo/client";
import { isBitSet } from "../../../utils";
import { detectTransitions } from "../../../utils/productionCalculations";

// ---- GraphQL ---------------------------------------------------------------

export const GET_REPORT_DEVICES = gql`
    query GetReportDevices($where: ShadowDeviceWhereInput) {
        shadowDevices(where: $where) {
            id
            name
            device
            parameters
            jobSchedules {
                id
                name
                start_time
                end_time
                target_units
                part {
                    id
                    name
                }
            }
        }
    }
`;

export const GET_REPORT_SHIFTS = gql`
    query GetReportShifts($plantId: Int!) {
        shifts(where: { plant_id: { equals: $plantId }, is_active: { equals: true } }) {
            id
            name
            start_time
            end_time
            runs_on
        }
    }
`;

export const GET_MAINTENANCE_SCHEDULES = gql`
    query GetMaintenanceSchedules($plantId: Int!) {
        maintenanceSchedules(
            where: { plant_id: { equals: $plantId }, is_active: { equals: true } }
        ) {
            id
            name
            description
            schedule_type
            schedule_attributes
            device_id
            device {
                id
                name
            }
        }
    }
`;

export const GET_DOWNTIME_REASONS = gql`
    query GetDowntimeReasons($plantId: Int!) {
        downtimeReasons(
            where: { plant_id: { equals: $plantId }, is_active: { equals: true } }
        ) {
            id
            name
            is_planned
        }
    }
`;

export const GET_DOWNTIME_EVENTS = gql`
    query GetDowntimeEvents($plantId: Int!, $from: DateTimeISO!, $to: DateTimeISO!) {
        downtimeEvents(
            where: {
                plant_id: { equals: $plantId }
                is_active: { equals: true }
                start_time: { gte: $from, lte: $to }
            }
        ) {
            id
            device_id
            start_time
            end_time
            duration_seconds
            source
            reason {
                id
                name
                is_planned
            }
        }
    }
`;

export const CREATE_DOWNTIME_EVENT = gql`
    mutation CreateDowntimeEvent($data: DowntimeEventCreateInput!) {
        createOneDowntimeEvent(data: $data) {
            id
        }
    }
`;

// ---- Datapoints REST fetch -------------------------------------------------

export async function fetchDatapointsForRange({
    workspace,
    authToken,
    deviceIds,
    startTime,
    endTime,
}) {
    const { scheme, host, port } = workspace.engine;
    const url = `${scheme}://${host}:${port}/api/v1/${workspace.name}/datapoints`;
    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
            devices: deviceIds,
            start_time: startTime,
            end_time: endTime,
            group_by_device: true,
            group_by_signal: true,
        }),
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch datapoints: ${response.status}`);
    }
    return response.json();
}

// ---- Signal helpers --------------------------------------------------------

// Bus the signal sits on: 0 GPIO, 1 UART, 2 I2C, 3 VIRT.
function busOf(signal, flags) {
    const lo = isBitSet(signal.type, flags.BIT_LOCATION_LO_BUS) ? 1 : 0;
    const hi = isBitSet(signal.type, flags.BIT_LOCATION_HI_BUS) ? 1 : 0;
    return (hi << 1) | lo;
}

const BUS_VIRT = 3;

// Signals that can carry production pulses: any physical input (GPIO, UART or
// I2C bus). Virtual signals are device telemetry (wifi_rssi, pwr), not
// production, so they are excluded here.
export function inputSignalsOf(device, types) {
    const signals = device.device?.runtime?.signals;
    const arr = Array.isArray(signals) ? signals : [];
    if (!types?.device?.flags) return arr.filter((s) => !s.name.startsWith("/virt/"));
    const flags = types.device.flags;
    return arr.filter(
        (sig) =>
            isBitSet(sig.type, flags.BIT_LOCATION_INPUT) && busOf(sig, flags) !== BUS_VIRT,
    );
}

// Analog inputs usable as a power channel. Virtual signals ARE allowed here —
// on the current device models the power reading arrives as /node0/pwr on the
// virtual bus. Named power channels sort first.
export function analogInputSignalsOf(device, types) {
    const signals = device.device?.runtime?.signals;
    const arr = Array.isArray(signals) ? signals : [];
    const named = (s) => /pwr|power|energy|kw/i.test(s.name);
    if (!types?.device?.flags) {
        return arr.filter((s) => named(s) || !s.name.startsWith("/virt/"));
    }
    const flags = types.device.flags;
    return arr
        .filter(
            (sig) =>
                isBitSet(sig.type, flags.BIT_LOCATION_INPUT) &&
                !isBitSet(sig.type, flags.BIT_LOCATION_DIGITAL),
        )
        .sort((a, b) => (named(b) ? 1 : 0) - (named(a) ? 1 : 0));
}

function signalThreshold(device, signal) {
    const pct = device.parameters?.[signal.name]?.minThreshold;
    return typeof pct === "number" ? (pct / 100) * 1024 : null;
}

function isSampleOn(sample, signal, device, types) {
    let isDigital = false;
    if (types?.device?.flags && typeof signal?.type === "number") {
        isDigital = isBitSet(signal.type, types.device.flags.BIT_LOCATION_DIGITAL);
    }
    if (isDigital) return parseInt(sample.value) === 0; // 0 = ON
    const threshold = signalThreshold(device, signal) ?? 512;
    return parseFloat(sample.value) > threshold;
}

// ---- Production ------------------------------------------------------------

// Transitions for one device across all its GPIO input signals, honouring the
// per-signal threshold overrides in ShadowDevice.parameters.
export function deviceTransitions(device, datapointsData, types) {
    const perSignal = datapointsData?.data?.[device.id] || {};
    const all = [];
    inputSignalsOf(device, types).forEach((signal) => {
        const points = perSignal[signal.name] || [];
        const transitions = detectTransitions(
            points,
            signal,
            signalThreshold(device, signal),
            types,
        );
        all.push(...transitions);
    });
    return all.sort((a, b) => a.timestamp - b.timestamp);
}

// Filters transitions to those inside job windows for the given part.
// partName null = all parts, counts every transition.
export function filterTransitionsByPart(transitions, jobSchedules, partName) {
    if (!partName) return transitions;
    const windows = (jobSchedules || [])
        .filter((j) => j.part?.name === partName)
        .map((j) => [new Date(j.start_time).getTime(), new Date(j.end_time).getTime()]);
    if (!windows.length) return [];
    return transitions.filter((t) => {
        const ms = t.timestamp * 1000;
        return windows.some(([s, e]) => ms >= s && ms <= e);
    });
}

// Share of one job's target that falls inside [startMs, endMs]. A job's target
// covers its whole run window, so a run only partly inside the report window
// contributes that fraction of its target — otherwise one day of a week-long
// run would be measured against the week's whole target. Unrounded; 0 when the
// job has no target, no overlap or a zero-length window.
export function proratedTarget(job, startMs, endMs) {
    if (job?.target_units == null) return 0;
    const js = new Date(job.start_time).getTime();
    const je = new Date(job.end_time).getTime();
    const jobSpan = je - js;
    if (!(jobSpan > 0) || je < startMs || js > endMs) return 0;
    const overlap = Math.min(je, endMs) - Math.max(js, startMs);
    return job.target_units * (overlap / jobSpan);
}

// Target for [startMs, endMs] from jobs on this device.
export function deviceTargetUnits(device, startMs, endMs) {
    return Math.round(
        (device.jobSchedules || []).reduce(
            (sum, job) => sum + proratedTarget(job, startMs, endMs),
            0,
        ),
    );
}

// Machine that finishes a job: the JobScheduleOperation with the highest
// sequence_no, since that step is where completed units are counted. Falls
// back to the job's own device_id when the route was never snapshotted.
export function jobLastStepDeviceId(job) {
    const ops = job?.operations;
    if (!Array.isArray(ops) || ops.length === 0) return job?.device_id ?? null;
    const last = ops.reduce((best, op) =>
        (op.sequence_no ?? -Infinity) > (best.sequence_no ?? -Infinity) ? op : best,
    );
    return last.device_id ?? job.device_id ?? null;
}

export function bucketCountsByDay(transitions, startMs, endMs) {
    const days = [];
    const d = new Date(startMs);
    d.setHours(0, 0, 0, 0);
    while (d.getTime() <= endMs) {
        days.push({
            key: d.toISOString().slice(0, 10),
            label: d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }),
            start: d.getTime(),
            end: d.getTime() + 86400000,
            count: 0,
        });
        d.setDate(d.getDate() + 1);
    }
    transitions.forEach((t) => {
        const ms = t.timestamp * 1000;
        const day = days.find((x) => ms >= x.start && ms < x.end);
        if (day) day.count += 1;
    });
    return days;
}

export function bucketCountsByHour(transitions, startMs, endMs) {
    const buckets = [];
    for (let t = startMs; t < endMs; t += 3600000) {
        buckets.push({ start: t, count: 0 });
    }
    transitions.forEach((tr) => {
        const ms = tr.timestamp * 1000;
        const idx = Math.floor((ms - startMs) / 3600000);
        if (idx >= 0 && idx < buckets.length) buckets[idx].count += 1;
    });
    return buckets;
}

// ---- Shifts ----------------------------------------------------------------

// Shift windows (ms) for a shift on a calendar date. start/end_time are
// seconds from midnight; runs_on is a Sunday-first day bitmask. Overnight
// shifts (end < start) spill into the next day.
export function shiftWindowOnDate(shift, date) {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    // runs_on bit 0 = Sunday ... bit 6 = Saturday, the same order as JS
    // getDay() and the Shift form's WeekDaysSelector (62 = Mon-Fri).
    const dow = dayStart.getDay();
    if (shift.runs_on != null && !(shift.runs_on & (1 << dow))) return null;
    const start = dayStart.getTime() + shift.start_time * 1000;
    let end = dayStart.getTime() + shift.end_time * 1000;
    if (shift.end_time <= shift.start_time) end += 86400000;
    return [start, end];
}

export function countInWindow(transitions, window) {
    if (!window) return 0;
    const [s, e] = window;
    return transitions.filter((t) => {
        const ms = t.timestamp * 1000;
        return ms >= s && ms <= e;
    }).length;
}

// ---- Downtime detection ----------------------------------------------------

export const DOWNTIME_GRACE_SECONDS = 300; // OFF shorter than this is idle, not downtime
export const SILENCE_AFTER_SECONDS = 1800; // no datapoints beyond this = silence downtime
export const DOWNTIME_SOURCE = { SIGNAL_OFF: 0, SILENCE: 1 };

// Detects downtime intervals for one device inside [startSec, endSec]:
// - OFF state on any production input signal lasting > grace
// - gaps with no datapoints at all lasting > SILENCE_AFTER_SECONDS
// Returns [{ device_id, start, end, duration, source }] with times in unix seconds.
/**
 * Stamps `duration` on detected events and puts them in chronological order.
 *
 * Every exit from detectDowntime goes through here. It used to be inline on the
 * final return, which the no-telemetry branch skipped by returning early — those
 * events reached the UI with `duration: undefined`, so the pill rendered "NaN min"
 * and tagging one was rejected with `duration_seconds of required type "Int!" was
 * not provided` (CPRN00117-907).
 *
 * duration_seconds is an Int! on the server, so the value is rounded, and clamped
 * at zero rather than sent negative if a detector ever hands back end < start.
 */
function finaliseDowntime(events) {
    return events
        .map((e) => ({
            ...e,
            duration: Math.max(0, Math.round(e.end - e.start)) || 0,
        }))
        .sort((a, b) => a.start - b.start);
}

export function detectDowntime(device, datapointsData, types, startSec, endSec) {
    const perSignal = datapointsData?.data?.[device.id] || {};
    const signals = inputSignalsOf(device, types);
    const events = [];

    // Merge all samples across signals for the silence check.
    const allSamples = [];
    signals.forEach((signal) => {
        (perSignal[signal.name] || []).forEach((p) => allSamples.push(p));
    });
    allSamples.sort((a, b) => a.time - b.time);

    if (allSamples.length === 0) {
        if (endSec - startSec > SILENCE_AFTER_SECONDS) {
            events.push({
                device_id: device.id,
                start: startSec,
                end: endSec,
                source: DOWNTIME_SOURCE.SILENCE,
            });
        }
        return finaliseDowntime(events);
    }

    // Silence gaps: between consecutive samples, plus range edges.
    const edges = [
        startSec,
        ...allSamples.map((p) => p.time),
        endSec,
    ];
    for (let i = 1; i < edges.length; i++) {
        if (edges[i] - edges[i - 1] > SILENCE_AFTER_SECONDS) {
            events.push({
                device_id: device.id,
                start: edges[i - 1],
                end: edges[i],
                source: DOWNTIME_SOURCE.SILENCE,
            });
        }
    }

    // OFF intervals on the busiest signal (most samples = the production signal).
    let primary = null;
    let primaryPoints = [];
    signals.forEach((signal) => {
        const pts = perSignal[signal.name] || [];
        if (pts.length > primaryPoints.length) {
            primary = signal;
            primaryPoints = pts;
        }
    });
    if (primary && primaryPoints.length > 1) {
        const sorted = [...primaryPoints].sort((a, b) => a.time - b.time);
        let offStart = null;
        sorted.forEach((sample) => {
            const on = isSampleOn(sample, primary, device, types);
            if (!on && offStart === null) offStart = sample.time;
            if (on && offStart !== null) {
                if (sample.time - offStart > DOWNTIME_GRACE_SECONDS) {
                    events.push({
                        device_id: device.id,
                        start: offStart,
                        end: sample.time,
                        source: DOWNTIME_SOURCE.SIGNAL_OFF,
                    });
                }
                offStart = null;
            }
        });
        if (offStart !== null && endSec - offStart > DOWNTIME_GRACE_SECONDS) {
            events.push({
                device_id: device.id,
                start: offStart,
                end: endSec,
                source: DOWNTIME_SOURCE.SIGNAL_OFF,
            });
        }
    }

    return finaliseDowntime(events);
}

// ---- Interval helpers ------------------------------------------------------

// Intervals are [start, end] pairs in unix seconds, the same unit as
// detectDowntime's start/end. The maths is unit-agnostic, so ms works too as
// long as intervals and windows agree.

// Sorted, non-overlapping union of the intervals. Overlapping and touching
// (end === next start) intervals merge; empty or inverted ones are dropped.
export function mergeIntervals(intervals) {
    const sorted = (intervals || [])
        .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e) && e > s)
        .map(([s, e]) => [s, e])
        .sort((a, b) => a[0] - b[0]);
    const merged = [];
    sorted.forEach(([s, e]) => {
        const last = merged[merged.length - 1];
        if (last && s <= last[1]) last[1] = Math.max(last[1], e);
        else merged.push([s, e]);
    });
    return merged;
}

// Parts of the intervals that fall inside any of the windows (e.g. downtime
// clipped to shift hours), merged so overlapping windows are not counted twice.
export function clipIntervals(intervals, windows) {
    const ins = mergeIntervals(intervals);
    const wins = mergeIntervals(windows);
    const out = [];
    ins.forEach(([s, e]) => {
        wins.forEach(([ws, we]) => {
            const cs = Math.max(s, ws);
            const ce = Math.min(e, we);
            if (ce > cs) out.push([cs, ce]);
        });
    });
    return mergeIntervals(out);
}

export function formatDuration(seconds) {
    if (seconds >= 3600) {
        const h = Math.floor(seconds / 3600);
        const m = Math.round((seconds % 3600) / 60);
        return `${h}h ${m}m`;
    }
    return `${Math.max(1, Math.round(seconds / 60))} min`;
}

// ---- Energy (raw trend — uncalibrated, "power index" only) -----------------

// Average raw value of the device's analog input per hour bucket.
// Values are the raw 0-1023 scale; no kW/kWh claims until calibration exists.
export function energyTrend(device, datapointsData, types, startSec, endSec, bucketSec = 3600) {
    const perSignal = datapointsData?.data?.[device.id] || {};
    // analogInputSignalsOf sorts named power channels first, so the first one
    // carrying samples wins; otherwise fall back to the busiest analog input.
    const analogs = analogInputSignalsOf(device, types);
    let signal = analogs.find((s) => (perSignal[s.name] || []).length > 0) || null;
    let points = signal ? perSignal[signal.name] : [];
    if (!signal) {
        analogs.forEach((s) => {
            const pts = perSignal[s.name] || [];
            if (pts.length > points.length) {
                signal = s;
                points = pts;
            }
        });
    }
    const buckets = [];
    for (let t = startSec; t < endSec; t += bucketSec) {
        buckets.push({ start: t, sum: 0, n: 0 });
    }
    points.forEach((p) => {
        const idx = Math.floor((p.time - startSec) / bucketSec);
        if (idx >= 0 && idx < buckets.length) {
            buckets[idx].sum += parseFloat(p.value);
            buckets[idx].n += 1;
        }
    });
    return {
        signalName: signal?.name || null,
        buckets: buckets.map((b) => ({
            start: b.start,
            avg: b.n ? b.sum / b.n : null,
        })),
    };
}

// ---- Maintenance -----------------------------------------------------------

export function maintenanceWindow(schedule) {
    const attrs = schedule.schedule_attributes || {};
    const from = attrs.from ? new Date(attrs.from) : null;
    const to = attrs.to ? new Date(attrs.to) : null;
    return { from, to };
}

export function maintenanceStatus(schedule, now = new Date()) {
    const { from, to } = maintenanceWindow(schedule);
    if (from && from > now) return "upcoming";
    if (from && to && from <= now && to >= now) return "in-progress";
    if (to && to < now) return "past";
    return "unscheduled";
}
